import React, { useState, useEffect, useMemo } from 'react';
import { api } from '../../api/api';
import NewUserCredentialsModal from './NewUserCredentialsModal';

const normalizeUserGroupIds = user => {
  if (Array.isArray(user?.groups) && user.groups.length > 0) {
    return user.groups
      .map(group => Number(group?.id))
      .filter(Number.isInteger)
      .map(String);
  }
  if (user?.group_id) return [String(user.group_id)];
  return [];
};

const UserManagement = ({ showNotification }) => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newUserCreds, setNewUserCreds] = useState(null);

  const [newUsername, setNewUsername] = useState('');
  const [newUserRole, setNewUserRole] = useState('');
  const [newUserGroups, setNewUserGroups] = useState([]);
  const [editingUserId, setEditingUserId] = useState(null);
  const [editingUserGroups, setEditingUserGroups] = useState([]);
  const [savingGroupsForUserId, setSavingGroupsForUserId] = useState(null);
  const [usernameFilter, setUsernameFilter] = useState('');
  const [usernameSortDirection, setUsernameSortDirection] = useState('asc');

  const fetchData = async () => {
    setLoading(true);
    const [userList, rolesData, groupsData] = await Promise.all([
      api.getUsers(),
      api.getRoles(),
      api.getGroups(),
    ]);
    setUsers(userList);
    setRoles(rolesData);
    setGroups(groupsData);
    if (rolesData.length > 0) {
      const userRole = rolesData.find(r => r.name === 'user');
      setNewUserRole(userRole ? userRole.id : rolesData[0].id);
    }
    if (groupsData.length > 0) {
      const noneGroup = groupsData.find(g => g.name === 'none');
      const defaultGroupId = noneGroup ? noneGroup.id : groupsData[0].id;
      setNewUserGroups([String(defaultGroupId)]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreateUser = async e => {
    e.preventDefault();
    if (!newUsername) return;
    // Ensure role and group IDs are integers
    const roleId = parseInt(newUserRole, 10);
    const groupIds = Array.from(
      new Set(
        (newUserGroups || [])
          .map(value => parseInt(value, 10))
          .filter(Number.isInteger)
      )
    );
    const primaryGroupId = groupIds[0] || null;
    const result = await api.createUser(
      newUsername,
      roleId,
      primaryGroupId,
      groupIds
    );
    if (result.success) {
      // Pass the password to the modal
      setNewUserCreds({ ...result.newUser });
      setNewUsername('');
      fetchData();
    } else {
      showNotification(result.message || 'Failed to create user.', 'error');
    }
  };

  const handleDeleteUser = async (userId, username) => {
    if (username === 'admin') {
      alert('The default admin user cannot be deleted.');
      return;
    }
    if (
      window.confirm(
        `Are you sure you want to permanently delete user "${username}"? This cannot be undone.`
      )
    ) {
      await api.deleteUser(userId);
      showNotification(`User "${username}" has been deleted.`, 'success');
      fetchData();
    }
  };

  const handleResetPassword = async (userId, username) => {
    if (
      window.confirm(
        `Are you sure you want to reset the password for ${username}?`
      )
    ) {
      const result = await api.resetUserPassword(userId);
      if (result.success) {
        setNewUserCreds({
          ...users.find(u => u.id === userId),
          password: result.newPassword,
        });
        showNotification(`Password for ${username} has been reset.`, 'success');
        fetchData();
      }
    }
  };

  const handleGroupsChange = async (userId, selectedGroupIds) => {
    const groupIds = Array.from(
      new Set(
        (selectedGroupIds || [])
          .map(value => parseInt(value, 10))
          .filter(Number.isInteger)
      )
    );
    await api.updateUser(userId, {
      group_ids: groupIds,
      group_id: groupIds[0] || null,
    });
    fetchData();
  };

  const toggleCreateGroup = groupId => {
    const groupKey = String(groupId);
    setNewUserGroups(prev =>
      prev.includes(groupKey)
        ? prev.filter(value => value !== groupKey)
        : [...prev, groupKey]
    );
  };

  const toggleEditingGroup = groupId => {
    const groupKey = String(groupId);
    setEditingUserGroups(prev =>
      prev.includes(groupKey)
        ? prev.filter(value => value !== groupKey)
        : [...prev, groupKey]
    );
  };

  const openUserGroupEditor = user => {
    setEditingUserId(user.id);
    setEditingUserGroups(normalizeUserGroupIds(user));
  };

  const saveUserGroups = async userId => {
    setSavingGroupsForUserId(userId);
    try {
      await handleGroupsChange(userId, editingUserGroups);
      showNotification('Groups updated.', 'success');
      setEditingUserId(null);
      setEditingUserGroups([]);
    } catch (error) {
      showNotification(error?.message || 'Failed to update groups.', 'error');
    } finally {
      setSavingGroupsForUserId(null);
    }
  };

  const visibleUsers = useMemo(() => {
    const query = usernameFilter.trim().toLowerCase();
    const filtered = query
      ? users.filter(user =>
          String(user?.username || '').toLowerCase().includes(query)
        )
      : users;
    const sorted = [...filtered].sort((a, b) =>
      String(a?.username || '').localeCompare(String(b?.username || ''))
    );
    return usernameSortDirection === 'desc' ? sorted.reverse() : sorted;
  }, [users, usernameFilter, usernameSortDirection]);

  if (loading) return <p>Loading users...</p>;

  return (
    <div className="space-y-8">
      {newUserCreds && (
        <NewUserCredentialsModal
          user={newUserCreds}
          onClose={() => setNewUserCreds(null)}
        />
      )}

      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Create New User
        </h3>
        <form
          onSubmit={handleCreateUser}
          className="grid grid-cols-4 items-end gap-4 p-4 bg-gray-50 rounded-lg border"
        >
          <div className="col-span-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Username
            </label>
            <input
              type="text"
              value={newUsername}
              onChange={e => setNewUsername(e.target.value)}
              className="form-input"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Role
            </label>
            <select
              value={newUserRole}
              onChange={e => setNewUserRole(e.target.value)}
              className="form-select"
            >
              {roles.map(role => (
                <option key={role.id} value={role.id}>
                  {role.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Groups
            </label>
            <div className="max-h-32 overflow-y-auto rounded-md border border-gray-300 bg-white p-2 space-y-1">
              {groups.map(group => (
                <label key={group.id} className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={newUserGroups.includes(String(group.id))}
                    onChange={() => toggleCreateGroup(group.id)}
                  />
                  <span>{group.name}</span>
                </label>
              ))}
            </div>
          </div>
          <button type="submit" className="nav-button-primary">
            Create User
          </button>
        </form>
      </div>
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Manage Users</h3>
        <div className="mb-3 flex flex-col gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3 sm:flex-row sm:items-end">
          <div className="w-full sm:max-w-sm">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Filter by Username
            </label>
            <input
              type="text"
              value={usernameFilter}
              onChange={e => setUsernameFilter(e.target.value)}
              placeholder="Type username..."
              className="form-input"
            />
          </div>
          <div className="w-full sm:w-52">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Sort Username
            </label>
            <select
              value={usernameSortDirection}
              onChange={e => setUsernameSortDirection(e.target.value)}
              className="form-select"
            >
              <option value="asc">A → Z</option>
              <option value="desc">Z → A</option>
            </select>
          </div>
        </div>
        <div className="overflow-x-auto border rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Username
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Groups
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {visibleUsers.map(user => (
                <tr key={user.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {user.username}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">
                    {user.role || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div className="space-y-2">
                      <div className="text-xs text-gray-600">
                        {normalizeUserGroupIds(user)
                          .map(id => groups.find(group => String(group.id) === id)?.name || id)
                          .join(', ') || 'No group assigned'}
                      </div>
                      {editingUserId === user.id ? (
                        <div className="rounded-md border border-gray-200 bg-gray-50 p-2 space-y-2">
                          <div className="max-h-28 overflow-y-auto space-y-1">
                            {groups.map(group => (
                              <label key={group.id} className="flex items-center gap-2 text-xs text-gray-700">
                                <input
                                  type="checkbox"
                                  checked={editingUserGroups.includes(String(group.id))}
                                  onChange={() => toggleEditingGroup(group.id)}
                                />
                                <span>{group.name}</span>
                              </label>
                            ))}
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => saveUserGroups(user.id)}
                              disabled={savingGroupsForUserId === user.id}
                              className="rounded bg-primary px-2 py-1 text-xs font-semibold text-white disabled:opacity-60"
                            >
                              {savingGroupsForUserId === user.id ? 'Saving...' : 'Save groups'}
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setEditingUserId(null);
                                setEditingUserGroups([]);
                              }}
                              className="rounded border border-gray-300 px-2 py-1 text-xs font-semibold text-gray-700"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => openUserGroupEditor(user)}
                          className="text-xs font-semibold text-primary hover:text-primary/80"
                        >
                          Edit groups
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {user.mustChangePassword ? (
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                        Password Reset
                      </span>
                    ) : (
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                        Active
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-4">
                    <button
                      onClick={() =>
                        handleResetPassword(user.id, user.username)
                      }
                      className="text-indigo-600 hover:text-indigo-900"
                    >
                      Reset Password
                    </button>
                    <button
                      onClick={() => handleDeleteUser(user.id, user.username)}
                      className="text-red-600 hover:text-red-900"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
              {visibleUsers.length === 0 && (
                <tr>
                  <td
                    colSpan={5}
                    className="px-6 py-8 text-center text-sm text-gray-500"
                  >
                    No users match your filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
export default UserManagement;
